import { useCallback, useMemo, useState } from 'react';

import { Build, Close as CloseIcon, Code, Delete, ErrorOutline as ErrorOutlineIcon, Folder, MoreVert, NoteAdd, OpenInNew, Palette, Publish, RocketLaunch, Settings, ViewComfy, Widgets } from '@mui/icons-material';
import WebIcon from '@mui/icons-material/Web';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { type ArtifactCategory, type ArtifactItem, ArtifactPanel, ArtifactStateBadge } from '@@common';
import { ConfirmDialog } from '@@components';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';

import { sortByNameCapital } from '../../../common/sortByName';
import { sortByTitleCapital } from '../../../common/sortByTitle';
import { uuid } from '../../../common/uuid';
import { ReactCodeLifecycleState, ReactCodeStatusModel } from '../../../api/api';
import { Bundle } from '../Models/Bundle';
import { ComponentTypes } from '../Models/ComponentTypes';
import { SharedComponentModel } from '../Models/SharedComponentModel';
import { ViewModel } from '../Models/ViewModel';
import { ComponentPath } from '../Utils/ComponentTreeUtils';
import { createRouteGroupedViewItems } from '../Utils/routeViewArtifactTree';
import { hasRouteParameters } from '../Utils/initialDesignerView';
import type { DesignerViewer } from '../Hooks/useAppDesignerPresence';
import { DesignerPresenceIndicator, viewersForItem } from './DesignerPresenceIndicator';
import { ViewRoutePath } from './ViewRoutePath';
import { CreateChildViewDialog } from './CreateChildViewDialog';

const EMPTY_VIEWERS: DesignerViewer[] = [];

const LOCAL_STORAGE_KEY = 'bundleSidePanel.width';
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 200;
const MAX_WIDTH = 800;
const TREE_NODE_SHARED_COMPONENTS = 'sharedComponents';
const TREE_NODE_VIEWS = 'views';
const TREE_NODE_DIALOGS = 'dialogs';
const TREE_NODE_EMBEDDED_RESOURCES = 'embeddedResources';
const TREE_NODE_THEMES = 'themes';
const TREE_NODE_REACT_SOURCE = 'reactSource';

export const ITEM_TYPE_SHARED_COMPONENT = 'sharedComponent' as const;
export const ITEM_TYPE_VIEW = 'view' as const;
export const ITEM_TYPE_EMBEDDED_RESOURCE = 'embeddedResource' as const;
export const ITEM_TYPE_THEME = 'theme' as const;
export const ITEM_TYPE_SETTINGS = 'settings' as const;
export const ITEM_TYPE_REACT_SOURCE_FILE = 'reactSourceFile' as const;
export const SETTINGS_ITEM_BUNDLE_SETTINGS = 'settings';

export type SelectedItemType =
	| typeof ITEM_TYPE_SHARED_COMPONENT
	| typeof ITEM_TYPE_VIEW
	| typeof ITEM_TYPE_EMBEDDED_RESOURCE
	| typeof ITEM_TYPE_THEME
	| typeof ITEM_TYPE_SETTINGS
	| typeof ITEM_TYPE_REACT_SOURCE_FILE;

export type SelectedItem = {
	type: SelectedItemType;
	id: string;
};

/**
 * Context for a component selection that is inside a SharedComponent (via ReferenceComponent).
 * Used to resolve the component from the SharedComponent's tree when displaying properties.
 */
export type SharedComponentSelectionContext = {
	/** ID of the SharedComponent containing the selected component */
	sharedComponentId: string;
	/** Path to the ReferenceComponent in the View (used to transform paths) */
	referenceComponentPath: ComponentPath;
};

export function encodeItemId(type: SelectedItemType, id: string) {
	return `${type}::${id}`;
}

export function decodeItemId(encoded: string): SelectedItem | null {
	const sep = encoded.indexOf('::');
	if (sep === -1) return null;
	const type = encoded.slice(0, sep) as SelectedItemType;
	const id = encoded.slice(sep + 2);
	return { type, id };
}

type AppArtifactPanelProps = {
	viewers?: DesignerViewer[];
	bundle: Bundle | null;
	selectedItem: SelectedItem | null;
	onSelectItem: (item: SelectedItem | null) => void;
	onDeleteItem: (item: SelectedItem) => void | Promise<void>;
	onBundleChanged: (bundle: Bundle) => void;
	reactSourceFiles: ReactSourceFileArtifact[];
	reactCodeStatus?: ReactCodeStatusModel;
	reactCodeOperationPending?: boolean;
	onCompileReactCode: () => void | Promise<void>;
	onPublishReactCode: () => void | Promise<void>;
	onBuildAndPublishReactCode: () => void | Promise<void>;
	onRenameReactSourceFile: (id: string, path: string) => void | Promise<void>;
	onRenameReactSourceFolder: (path: string, name: string) => void | Promise<void>;
	onCreateReactSourceFile: (path: string) => void | Promise<void>;
	isMobile?: boolean;
	readOnly?: boolean;
};

export type ReactSourceFileArtifact = {
	id: string;
	path: string;
	tags?: { name: string; value: string }[];
};

const GENERATED_ARTIFACT_TAG_NAME = 'RandolArtifactKind';

export function isGeneratedReactSourceFile(file: ReactSourceFileArtifact): boolean {
	return file.tags?.some((tag) => tag.name === GENERATED_ARTIFACT_TAG_NAME) ?? false;
}

function createReactSourceTree(
	files: ReactSourceFileArtifact[],
	onRenameFile: (id: string, path: string) => void | Promise<void>,
	onRenameFolder: (path: string, name: string) => void | Promise<void>,
	onCreateFile: (directory: string) => void,
	onDeleteFile: (id: string) => void | Promise<void>,
): ArtifactItem[] {
	const rootItems: ArtifactItem[] = [];
	const folders = new Map<string, ArtifactItem>();
	const generatedPaths = files.filter(isGeneratedReactSourceFile).map((file) => file.path.toLowerCase());

	for (const file of [...files].sort((left, right) => left.path.localeCompare(right.path))) {
		const segments = file.path.split(/[\\/]+/).filter(Boolean);
		if (segments.length === 0) continue;

		let siblings = rootItems;
		let parentPath = '';
		for (const segment of segments.slice(0, -1)) {
			parentPath = parentPath ? `${parentPath}/${segment}` : segment;
			let folder = folders.get(parentPath);
			if (!folder) {
				const folderPath = parentPath;
				const containsGeneratedApi = generatedPaths.some((path) => path.startsWith(`${folderPath.toLowerCase()}/`));
				folder = {
					id: `react-source-folder/${folderPath}`,
					label: segment,
					icon: <Folder fontSize="small" />,
					countAsArtifact: false,
					children: [],
					onRename: containsGeneratedApi ? undefined : (name) => onRenameFolder(folderPath, name),
					onAddChild: () => onCreateFile(folderPath),
				};
				folders.set(parentPath, folder);
				siblings.push(folder);
			}
			siblings = folder.children!;
		}

		siblings.push({
			id: encodeItemId(ITEM_TYPE_REACT_SOURCE_FILE, file.id),
			label: segments[segments.length - 1],
			onDelete: isGeneratedReactSourceFile(file) ? undefined : () => onDeleteFile(file.id),
			onRename: isGeneratedReactSourceFile(file) ? undefined : (name) => {
				const directory = segments.slice(0, -1).join('/');
				return onRenameFile(file.id, directory ? `${directory}/${name}` : name);
			},
		});
	}

	return rootItems;
}

export function AppArtifactPanel({
	viewers = EMPTY_VIEWERS,
	bundle,
	selectedItem,
	onSelectItem,
	onDeleteItem,
	onBundleChanged,
	reactSourceFiles,
	reactCodeStatus,
	reactCodeOperationPending = false,
	onCompileReactCode,
	onPublishReactCode,
	onBuildAndPublishReactCode,
	onRenameReactSourceFile,
	onRenameReactSourceFolder,
	onCreateReactSourceFile,
	isMobile = false,
	readOnly = false,
}: AppArtifactPanelProps) {
	const [itemPendingDelete, setItemPendingDelete] = useState<(SelectedItem & { name: string }) | null>(null);
	const [viewPendingSelection, setViewPendingSelection] = useState<ViewModel | null>(null);
	const [childViewParent, setChildViewParent] = useState<ViewModel | null>(null);
	const [viewActionMenu, setViewActionMenu] = useState<{ view: ViewModel; anchor: HTMLElement } | null>(null);
	const [createReactFileDialog, setCreateReactFileDialog] = useState<{ directory: string; name: string } | null>(null);
	const [reactBuildErrorsAnchor, setReactBuildErrorsAnchor] = useState<HTMLElement | null>(null);
	const [reactActionsAnchor, setReactActionsAnchor] = useState<HTMLElement | null>(null);
	const reactBuildErrors = reactCodeStatus?.diagnostics?.length ? reactCodeStatus.diagnostics : ['The React build failed without diagnostic details.'];
	const handleOpenCreateReactFile = useCallback((directory = '') => setCreateReactFileDialog({ directory, name: '' }), []);
	const handleCreateReactFile = useCallback(() => {
		if (!createReactFileDialog?.name.trim()) return;
		const name = createReactFileDialog.name.trim();
		const path = `${createReactFileDialog.directory ? `${createReactFileDialog.directory}/` : ''}${name}`;
		void onCreateReactSourceFile(path);
		setCreateReactFileDialog(null);
	}, [createReactFileDialog, onCreateReactSourceFile]);

	const handleRequestDeleteItem = useCallback((item: SelectedItem & { name: string }) => {
		setItemPendingDelete(item);
	}, []);

	const handleConfirmDeleteItem = useCallback(() => {
		if (!itemPendingDelete) return;
		onDeleteItem({ type: itemPendingDelete.type, id: itemPendingDelete.id });
		setItemPendingDelete(null);
	}, [itemPendingDelete, onDeleteItem]);

	const handleCancelDeleteItem = useCallback(() => {
		setItemPendingDelete(null);
	}, []);

	const handleAddView = useCallback(() => {
		if (!bundle) return;
		const id = uuid();
		const newView: ViewModel = { Id: id, Title: 'New View', Children: [], Layout: {}, Type: 'View' };
		onBundleChanged({ ...bundle, Views: [...bundle.Views, newView] });
		onSelectItem({ type: ITEM_TYPE_VIEW, id });
	}, [bundle, onBundleChanged, onSelectItem]);

	const handleAddSharedComponent = useCallback(() => {
		if (!bundle) return;
		const newSharedComponent: SharedComponentModel = {
			Id: uuid(),
			Name: 'New Component',
			Component: { Id: uuid(), Type: ComponentTypes.StackContainer, Layout: {}, Children: [] },
		};
		onBundleChanged({ ...bundle, SharedComponents: [...bundle.SharedComponents, newSharedComponent] });
		onSelectItem({ type: ITEM_TYPE_SHARED_COMPONENT, id: newSharedComponent.Id });
	}, [bundle, onBundleChanged, onSelectItem]);

	const handleAddEmbeddedResource = useCallback(() => {
		if (!bundle) return;
		const id = uuid();
		const newResource = { Id: id, Name: 'New Resource', Content: '' };
		onBundleChanged({ ...bundle, EmbeddedResources: [...bundle.EmbeddedResources, newResource] });
		onSelectItem({ type: ITEM_TYPE_EMBEDDED_RESOURCE, id });
	}, [bundle, onBundleChanged, onSelectItem]);

	const handleAddTheme = useCallback(() => {
		if (!bundle) return;
		const id = uuid();
		const newTheme = { Id: id, Name: `Theme ${(bundle.Themes?.length ?? 0) + 1}`, IsDefault: false, Theme: {} };
		onBundleChanged({ ...bundle, Themes: [...(bundle.Themes ?? []), newTheme] });
		onSelectItem({ type: ITEM_TYPE_THEME, id });
	}, [bundle, onBundleChanged, onSelectItem]);

	const sortedSharedComponents = useMemo(() => [...(bundle?.SharedComponents || [])].sort(sortByNameCapital), [bundle?.SharedComponents]);
	const sortedViews = useMemo(() => [...(bundle?.Views || [])].sort(sortByTitleCapital), [bundle?.Views]);
	const sortedEmbeddedResources = useMemo(() => [...(bundle?.EmbeddedResources || [])].sort(sortByNameCapital), [bundle?.EmbeddedResources]);
	const reactCodeState = reactCodeStatus?.state ?? ReactCodeLifecycleState.Empty;
	const isReactBuildFailed = reactCodeState === ReactCodeLifecycleState.Failed;
	const isReactCodePublishing = reactCodeOperationPending && reactCodeState === ReactCodeLifecycleState.Compiled;
	const isReactCodeCompiling = reactCodeState === ReactCodeLifecycleState.Compiling || (reactCodeOperationPending && !isReactCodePublishing);
	const isReactCodeBusy = isReactCodeCompiling || isReactCodePublishing;
	const compileReactCodeTooltip =
		reactSourceFiles.length === 0
			? 'Add a React source file before compiling'
			: isReactCodeCompiling
				? 'React compilation is in progress'
				: isReactCodePublishing
					? 'React code is being published'
					: 'Compile React code';
	const publishReactCodeTooltip =
		isReactCodePublishing
			? 'React code is being published'
			: reactCodeState !== ReactCodeLifecycleState.Compiled
				? 'Compile React code successfully before publishing'
				: 'Publish compiled React code';
	const reactCodeStatusColor =
		reactCodeState === ReactCodeLifecycleState.Published
			? 'success.main'
			: reactCodeState === ReactCodeLifecycleState.Compiled
				? 'info.main'
				: reactCodeState === ReactCodeLifecycleState.Failed
					? 'error.main'
					: reactCodeState === ReactCodeLifecycleState.Draft || reactCodeState === ReactCodeLifecycleState.Modified
						? 'warning.main'
						: reactCodeState === ReactCodeLifecycleState.Compiling
							? 'info.main'
							: 'text.disabled';
	const createViewArtifactItem = useCallback(
		(view: ViewModel): ArtifactItem => ({
			id: encodeItemId(ITEM_TYPE_VIEW, view.Id),
			label: view.Title || view.Id,
			subtitle: view.RoutePath ? `Path: ${view.RoutePath}` : view.Dialog ? 'Dialog' : undefined,
			content: (
				<Stack direction="row" alignItems="center" spacing={0.5}>
					{view.Dialog ? <OpenInNew fontSize="small" /> : <WebIcon fontSize="small" />}
					<Stack sx={{ flex: 1 }}>
						<Stack direction="row" alignItems="center" spacing={0.5}>
							<Typography variant="body2">{view.Title || view.Id}</Typography>
							<DesignerPresenceIndicator viewers={viewersForItem(viewers, ITEM_TYPE_VIEW, view.Id)} />
						</Stack>
						{view.RoutePath ? <ViewRoutePath routePath={view.RoutePath} /> : view.Dialog ? <Typography variant="caption" color="text.secondary">Dialog</Typography> : null}
					</Stack>
					{!readOnly && !view.Dialog && (
						<Tooltip title={view.RoutePath?.trim() ? 'Add child view' : 'Set this view’s route path before adding a child view'}>
							<span>
								<IconButton size="small" disabled={!view.RoutePath?.trim()} aria-label={`Add child view to ${view.Title || view.Id}`}
									onClick={(event) => { event.stopPropagation(); setChildViewParent(view); }}>
									<AddCircleIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					{!readOnly && <IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							setViewActionMenu({ view, anchor: e.currentTarget });
						}}
						aria-label={`${view.Title || view.Id} actions`}
					>
						<MoreVert fontSize="small" />
					</IconButton>}
				</Stack>
			),
		}),
		[readOnly, viewers],
	);
	const routeGroupedViews = useMemo(
		() => createRouteGroupedViewItems(sortedViews.filter((view) => !view.Dialog), createViewArtifactItem),
		[sortedViews, createViewArtifactItem],
	);

	const sortedThemes = useMemo(
		() => [...(bundle?.Themes ?? [])].sort((left, right) => Number(right.IsDefault) - Number(left.IsDefault) || left.Name.localeCompare(right.Name)),
		[bundle?.Themes],
	);

	// Static items
	const staticItems: ArtifactItem[] = useMemo(
		() => [
			{
				id: encodeItemId(ITEM_TYPE_SETTINGS, SETTINGS_ITEM_BUNDLE_SETTINGS),
				label: 'Settings',
				icon: <Settings fontSize="small" />,
			},
		],
		[],
	);

	// Categories with items
	const categories: ArtifactCategory[] = useMemo(
		() => [
			// Themes
			{
				id: TREE_NODE_THEMES,
				label: 'Themes',
				icon: <Palette fontSize="small" />,
				items: sortedThemes.map((theme) => ({
					id: encodeItemId(ITEM_TYPE_THEME, theme.Id),
					label: theme.Name,
					subtitle: theme.IsDefault ? 'Default' : undefined,
					onDelete:
						!readOnly && sortedThemes.length > 1
							? () => handleRequestDeleteItem({ type: ITEM_TYPE_THEME, id: theme.Id, name: theme.Name })
							: undefined,
				})),
				onAddItem: readOnly ? undefined : handleAddTheme,
				addItemLabel: readOnly ? undefined : 'Add theme',
			},
			// Media
			{
				id: TREE_NODE_EMBEDDED_RESOURCES,
				label: 'Media',
				icon: <Folder fontSize="small" />,
				items: sortedEmbeddedResources.map((resource) => ({
					id: encodeItemId(ITEM_TYPE_EMBEDDED_RESOURCE, resource.Id),
					label: resource.Name || resource.Id,
					onDelete: readOnly ? undefined : () =>
						handleRequestDeleteItem({
							type: ITEM_TYPE_EMBEDDED_RESOURCE,
							id: resource.Id,
							name: resource.Name || resource.Id,
						}),
				})),
				onAddItem: readOnly ? undefined : handleAddEmbeddedResource,
				addItemLabel: readOnly ? undefined : 'Add media',
			},
			// Shared Components
			{
				id: TREE_NODE_SHARED_COMPONENTS,
				label: 'Shared Components',
				icon: <Widgets fontSize="small" />,
				items: sortedSharedComponents.map((component) => ({
					id: encodeItemId(ITEM_TYPE_SHARED_COMPONENT, component.Id),
					label: component.Name || component.Id,
					onDelete: readOnly ? undefined : () =>
						handleRequestDeleteItem({
							type: ITEM_TYPE_SHARED_COMPONENT,
							id: component.Id,
							name: component.Name || component.Id,
						}),
				})),
				onAddItem: readOnly ? undefined : handleAddSharedComponent,
				addItemLabel: readOnly ? undefined : 'Add shared component',
			},
			// Views
			{
				id: TREE_NODE_VIEWS,
				label: 'Views',
				icon: <ViewComfy fontSize="small" />,
				items: routeGroupedViews,
				onAddItem: readOnly ? undefined : handleAddView,
				addItemLabel: readOnly ? undefined : 'Add view',
			},
			{
				id: TREE_NODE_DIALOGS,
				parentId: TREE_NODE_VIEWS,
				label: 'Dialogs',
				icon: <OpenInNew fontSize="small" />,
				items: sortedViews.filter((view) => view.Dialog).map(createViewArtifactItem),
				emptyLabel: 'No dialogs',
			},
			{
				id: TREE_NODE_REACT_SOURCE,
				label: 'React Code',
				icon: <Code fontSize="small" />,
				endAdornment: (
					<Stack direction="row" alignItems="center" spacing={0.25}>
						<ArtifactStateBadge
							variant="chip"
							label={isReactCodePublishing ? 'Publishing' : isReactCodeCompiling ? 'Compiling' : reactCodeState}
							loading={isReactCodeBusy}
							color={isReactCodeBusy ? 'info.main' : reactCodeStatusColor}
							tooltip={isReactCodeBusy ? (isReactCodePublishing ? publishReactCodeTooltip : compileReactCodeTooltip) : isReactBuildFailed ? 'View React build errors' :
								reactCodeStatus?.diagnostics?.join('\n') ||
								(reactCodeState === ReactCodeLifecycleState.Modified
									? 'React source has changed since the last published build. Compile and publish to update the runtime.'
									: `React Code: ${reactCodeState}`)
							}
							onClick={isReactBuildFailed && !isReactCodeBusy ? (event) => setReactBuildErrorsAnchor(event.currentTarget) : undefined}
							ariaLabel={isReactBuildFailed && !isReactCodeBusy ? 'Show React build errors' : undefined}
						/>
						<Tooltip title="React code actions">
							<IconButton size="small" aria-label="React code actions" aria-haspopup="menu"
								aria-controls={reactActionsAnchor ? 'react-code-actions' : undefined}
								aria-expanded={Boolean(reactActionsAnchor)}
								onClick={(event) => { event.stopPropagation(); setReactActionsAnchor(event.currentTarget); }}>
								<MoreVert fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				),
				items: readOnly
					? reactSourceFiles.map((file) => ({ id: encodeItemId(ITEM_TYPE_REACT_SOURCE_FILE, file.id), label: file.path.split(/[\\/]+/).pop() ?? file.path }))
					: createReactSourceTree(reactSourceFiles, onRenameReactSourceFile, onRenameReactSourceFolder, handleOpenCreateReactFile,
						(id) => onDeleteItem({ type: ITEM_TYPE_REACT_SOURCE_FILE, id })),
			},
		],
		[
			reactActionsAnchor,
			isReactCodeBusy,
			isReactCodeCompiling,
			isReactCodePublishing,
			compileReactCodeTooltip,
			publishReactCodeTooltip,
			isReactBuildFailed,
			reactSourceFiles,
			reactCodeState,
			reactCodeStatus?.diagnostics,
			reactCodeStatusColor,
			sortedSharedComponents,
			sortedViews,
			routeGroupedViews,
			createViewArtifactItem,
			sortedEmbeddedResources,
			sortedThemes,
			handleAddTheme,
			handleAddSharedComponent,
			handleAddView,
			handleAddEmbeddedResource,
			handleRequestDeleteItem,
			handleOpenCreateReactFile,
			onRenameReactSourceFile,
			onRenameReactSourceFolder,
			onDeleteItem,
			readOnly,
		],
	);

	const presenceCategories = useMemo(() => {
		const decorate = (items: ArtifactItem[]): ArtifactItem[] => items.map((item) => {
			const selection = decodeItemId(item.id);
			const indicator = selection ? <DesignerPresenceIndicator viewers={viewersForItem(viewers, selection.type, selection.id)} /> : null;
			return {
				...item,
				children: item.children ? decorate(item.children) : undefined,
				labelAdornment: <>{item.labelAdornment}{indicator}</>,
			};
		});
		return categories.map((category) => ({ ...category, items: decorate(category.items) }));
	}, [categories, viewers]);

	// Custom search filter for views (search in both Title and RoutePath)
	const searchFilter = useCallback((item: ArtifactItem, searchTerm: string) => {
		const lowerSearch = searchTerm.toLowerCase();
		return item.label.toLowerCase().includes(lowerSearch) || (item.subtitle?.toLowerCase().includes(lowerSearch) ?? false);
	}, []);

	const handleSelectedItemsChange = useCallback(
		(itemId: string | null) => {
			if (!itemId) {
				onSelectItem(null);
				return;
			}
			const decoded = decodeItemId(itemId);
			const view = decoded?.type === ITEM_TYPE_VIEW ? bundle?.Views.find((candidate) => candidate.Id === decoded.id) : undefined;
			if (view && hasRouteParameters(view.RoutePath)) {
				setViewPendingSelection(view);
				return;
			}
			if (decoded) onSelectItem(decoded);
		},
		[bundle, onSelectItem],
	);

	return (
		<>
			{childViewParent && bundle && !readOnly && (
				<CreateChildViewDialog parent={childViewParent} views={bundle.Views} onClose={() => setChildViewParent(null)}
					onCreate={(view) => {
						onBundleChanged({ ...bundle, Views: [...bundle.Views, view] });
						onSelectItem({ type: ITEM_TYPE_VIEW, id: view.Id });
						setChildViewParent(null);
					}} />
			)}
			<ConfirmDialog
				open={viewPendingSelection !== null}
				title="Open view without route parameters?"
				message={`“${viewPendingSelection?.Title ?? ''}” requires values for its route (${viewPendingSelection?.RoutePath ?? ''}). Open it from a parent listing page so its link supplies those values. Opening it directly may show missing data or errors.`}
				confirmText="Open anyway"
				onConfirm={() => {
					if (viewPendingSelection && bundle?.Views.some((view) => view.Id === viewPendingSelection.Id)) {
						onSelectItem({ type: ITEM_TYPE_VIEW, id: viewPendingSelection.Id });
					}
					setViewPendingSelection(null);
				}}
				onCancel={() => setViewPendingSelection(null)}
			/>
			<ArtifactPanel
				title="Artifacts"
				titleIcon={<Folder fontSize="small" />}
				categories={presenceCategories}
				staticItems={staticItems}
				selectedItemId={selectedItem ? encodeItemId(selectedItem.type, selectedItem.id) : null}
				onSelectItem={handleSelectedItemsChange}
				searchPlaceholder="Search artifacts…"
				searchFilter={searchFilter}
				width={isMobile ? '100%' : undefined}
				storageKey={isMobile ? undefined : LOCAL_STORAGE_KEY}
				defaultWidth={DEFAULT_WIDTH}
				minWidth={MIN_WIDTH}
				maxWidth={MAX_WIDTH}
			/>
			<Menu anchorEl={viewActionMenu?.anchor ?? null} open={Boolean(viewActionMenu)} onClose={() => setViewActionMenu(null)}>
				<MenuItem onClick={() => {
					if (viewActionMenu && !readOnly) {
						handleRequestDeleteItem({ type: ITEM_TYPE_VIEW, id: viewActionMenu.view.Id, name: viewActionMenu.view.Title || viewActionMenu.view.Id });
					}
					setViewActionMenu(null);
				}} disabled={readOnly}>
					<ListItemIcon><Delete fontSize="small" /></ListItemIcon>
					<ListItemText>Delete</ListItemText>
				</MenuItem>
			</Menu>
			<Menu id="react-code-actions" anchorEl={reactActionsAnchor} open={Boolean(reactActionsAnchor)} onClose={() => setReactActionsAnchor(null)}>
				<MenuItem disabled={readOnly} onClick={() => { setReactActionsAnchor(null); handleOpenCreateReactFile(); }}>
					<ListItemIcon><NoteAdd fontSize="small" /></ListItemIcon>
					<ListItemText>Add React source file</ListItemText>
				</MenuItem>
				<Divider />
				<MenuItem title={compileReactCodeTooltip} disabled={readOnly || reactSourceFiles.length === 0 || isReactCodeCompiling || isReactCodePublishing}
					onClick={() => { setReactActionsAnchor(null); void onCompileReactCode(); }}>
					<ListItemIcon>{isReactCodeCompiling ? <CircularProgress size={18} /> : <Build fontSize="small" />}</ListItemIcon>
					<ListItemText>Compile React code</ListItemText>
				</MenuItem>
				<MenuItem title={publishReactCodeTooltip} disabled={readOnly || reactCodeState !== ReactCodeLifecycleState.Compiled || reactCodeOperationPending}
					onClick={() => { setReactActionsAnchor(null); void onPublishReactCode(); }}>
					<ListItemIcon>{isReactCodePublishing ? <CircularProgress size={18} /> : <Publish fontSize="small" />}</ListItemIcon>
					<ListItemText>Publish React code</ListItemText>
				</MenuItem>
				<MenuItem disabled={readOnly || reactSourceFiles.length === 0 || reactCodeOperationPending || isReactCodeCompiling}
					onClick={() => { setReactActionsAnchor(null); void onBuildAndPublishReactCode(); }}>
					<ListItemIcon><RocketLaunch fontSize="small" /></ListItemIcon>
					<ListItemText>Build and publish React code</ListItemText>
				</MenuItem>
			</Menu>
			<Popover
				open={Boolean(reactBuildErrorsAnchor)}
				anchorEl={reactBuildErrorsAnchor}
				onClose={() => setReactBuildErrorsAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Paper
					elevation={8}
					sx={{ width: 'min(720px, calc(100vw - 48px))', maxHeight: 'min(60vh, 520px)', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}
				>
					<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
						<Stack direction="row" spacing={1.25} alignItems="flex-start">
							<ErrorOutlineIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
							<Box>
								<Stack direction="row" spacing={1} alignItems="center">
									<Typography fontWeight={600}>React build failed</Typography>
									<Typography variant="caption" color="text.secondary">{reactBuildErrors.length}</Typography>
								</Stack>
								<Typography variant="body2" color="text.secondary">Review the build errors below.</Typography>
							</Box>
						</Stack>
						<Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
							<CopyToClipboardButton value={reactBuildErrors.join('\n')} title="Copy all errors" edge={false} sx={{ color: 'text.secondary' }} />
							<Tooltip title="Close build errors">
								<IconButton aria-label="Close build errors" size="small" onClick={() => setReactBuildErrorsAnchor(null)}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
					</Stack>
					<Box sx={{ maxHeight: '42vh', overflow: 'auto', pr: 1 }}>
						<List dense>
							{reactBuildErrors.map((message, index) => (
								<ListItem
									key={`${index}-${message}`}
									alignItems="flex-start"
									secondaryAction={<CopyToClipboardButton value={message} title="Copy error" edge={false} sx={{ mt: -0.5 }} />}
								>
									<ListItemIcon sx={{ minWidth: 40 }}><ErrorOutlineIcon color="error" /></ListItemIcon>
									<ListItemText
										slotProps={{ primary: { variant: 'body2' } }}
										primary={message}
										sx={{ '& .MuiListItemText-primary': { overflowWrap: 'anywhere' } }}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				</Paper>
			</Popover>
			<Dialog open={Boolean(createReactFileDialog)} onClose={() => setCreateReactFileDialog(null)} maxWidth="xs" fullWidth>
				<DialogTitle>New React source file</DialogTitle>
				<DialogContent>
					{createReactFileDialog?.directory && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{createReactFileDialog.directory}/</Typography>}
					<TextField
						autoFocus
						fullWidth
						margin="dense"
						label="File name"
						helperText="Use .tsx, .ts, or .css"
						value={createReactFileDialog?.name ?? ''}
						onChange={(event) => setCreateReactFileDialog((current) => current ? { ...current, name: event.target.value } : current)}
						onKeyDown={(event) => { if (event.key === 'Enter' && createReactFileDialog?.name.trim()) handleCreateReactFile(); }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateReactFileDialog(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleCreateReactFile} disabled={!createReactFileDialog?.name.trim()}>Create</Button>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				open={!!itemPendingDelete}
				title="Delete Item"
				message={itemPendingDelete ? `Are you sure you want to delete "${itemPendingDelete.name}"? This action cannot be undone.` : ''}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteItem}
				onCancel={handleCancelDeleteItem}
			/>
		</>
	);
}
